/**
 * Batch Question Generation
 *
 * Configure and start batch question generation jobs for sectors.
 * Allows selection of multiple wheels and question types with custom parameters.
 *
 * Date: 2025-11-15
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sparkles,
  Play,
  AlertCircle,
  CheckCircle2,
  Settings,
  Package,
  TrendingUp,
  Users,
  Scale,
} from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { useToast } from '@/components/ui/use-toast';

interface QuestionWheel {
  id: string;
  name: string;
  description?: string;
  category?: string;
  question_count: number;
}

interface QuestionType {
  id: string;
  name: string;
  description: string;
  priority: number;
  defaultLimit: number;
  parameters: {
    name: string;
    type: 'number';
    defaultValue: number;
    min?: number;
    max?: number;
  }[];
}

const QUESTION_TYPES: QuestionType[] = [
  {
    id: 'PC-PB',
    name: 'Product-Benefit Questions',
    description: 'Questions combining product categories with perceived benefits',
    priority: 70,
    defaultLimit: 50,
    parameters: [{ name: 'limit', type: 'number', defaultValue: 50, min: 1, max: 100 }],
  },
  {
    id: 'PC-PA',
    name: 'Product-Attribute Questions',
    description: 'Questions about product attributes and characteristics',
    priority: 65,
    defaultLimit: 50,
    parameters: [{ name: 'limit', type: 'number', defaultValue: 50, min: 1, max: 100 }],
  },
  {
    id: 'PC-TR',
    name: 'Product-Trend Questions',
    description: 'Questions about industry trends and innovations',
    priority: 60,
    defaultLimit: 20,
    parameters: [{ name: 'limit', type: 'number', defaultValue: 20, min: 1, max: 50 }],
  },
  {
    id: 'PC-PB-CMP',
    name: 'Product Comparison by Benefit',
    description: 'Compare products based on specific benefits',
    priority: 85,
    defaultLimit: 10,
    parameters: [
      { name: 'limit', type: 'number', defaultValue: 10, min: 1, max: 50 },
      { name: 'productsPerComparison', type: 'number', defaultValue: 5, min: 2, max: 10 },
    ],
  },
  {
    id: 'PC-BP-PB-CMP',
    name: 'Persona-Benefit Comparison',
    description: 'Compare products for specific buyer personas and benefits',
    priority: 90,
    defaultLimit: 10,
    parameters: [
      { name: 'limit', type: 'number', defaultValue: 10, min: 1, max: 50 },
      { name: 'productsPerComparison', type: 'number', defaultValue: 3, min: 2, max: 10 },
    ],
  },
  {
    id: 'PB-RANK',
    name: 'Rank by Benefit',
    description: 'Rank top products for specific benefits',
    priority: 80,
    defaultLimit: 10,
    parameters: [
      { name: 'limit', type: 'number', defaultValue: 10, min: 1, max: 50 },
      { name: 'topN', type: 'number', defaultValue: 10, min: 3, max: 20 },
    ],
  },
  {
    id: 'BP-RANK',
    name: 'Rank by Persona',
    description: 'Rank top products for buyer personas',
    priority: 85,
    defaultLimit: 10,
    parameters: [
      { name: 'limit', type: 'number', defaultValue: 10, min: 1, max: 50 },
      { name: 'topN', type: 'number', defaultValue: 10, min: 3, max: 20 },
    ],
  },
];

export default function BatchQuestionGeneration() {
  const { sectorId } = useParams<{ sectorId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [wheels, setWheels] = useState<QuestionWheel[]>([]);
  const [selectedWheels, setSelectedWheels] = useState<string[]>([]);
  const [selectedQuestionTypes, setSelectedQuestionTypes] = useState<string[]>([]);
  const [parameters, setParameters] = useState<Record<string, Record<string, number>>>({});
  const [sectorName, setSectorName] = useState<string>('');

  useEffect(() => {
    loadData();
  }, [sectorId]);

  useEffect(() => {
    // Initialize parameters with default values
    const defaultParams: Record<string, Record<string, number>> = {};
    QUESTION_TYPES.forEach((qt) => {
      defaultParams[qt.id] = {};
      qt.parameters.forEach((param) => {
        defaultParams[qt.id][param.name] = param.defaultValue;
      });
    });
    setParameters(defaultParams);
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load sector details
      const sectorResponse = await authFetch(`/api/sectors/${sectorId}`);
      const sectorData = await sectorResponse.json();
      setSectorName(sectorData.sector?.name || 'Unknown Sector');

      // Load question wheels
      const wheelsResponse = await authFetch(`/api/sectors/${sectorId}/question-wheels`);
      const wheelsData = await wheelsResponse.json();
      setWheels(wheelsData.wheels || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load sector data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleWheelToggle = (wheelId: string) => {
    setSelectedWheels((prev) =>
      prev.includes(wheelId)
        ? prev.filter((id) => id !== wheelId)
        : [...prev, wheelId]
    );
  };

  const handleQuestionTypeToggle = (questionTypeId: string) => {
    setSelectedQuestionTypes((prev) =>
      prev.includes(questionTypeId)
        ? prev.filter((id) => id !== questionTypeId)
        : [...prev, questionTypeId]
    );
  };

  const handleParameterChange = (questionTypeId: string, paramName: string, value: number) => {
    setParameters((prev) => ({
      ...prev,
      [questionTypeId]: {
        ...prev[questionTypeId],
        [paramName]: value,
      },
    }));
  };

  const calculateTotalItems = () => {
    return selectedWheels.length * selectedQuestionTypes.length;
  };

  const estimateTotalQuestions = () => {
    let total = 0;
    selectedQuestionTypes.forEach((qtId) => {
      const qt = QUESTION_TYPES.find((q) => q.id === qtId);
      if (qt) {
        const limit = parameters[qtId]?.limit || qt.defaultLimit;
        total += limit * selectedWheels.length;
      }
    });
    return total;
  };

  const handleStartBatch = async () => {
    if (selectedWheels.length === 0) {
      toast({
        title: 'Validation Error',
        description: 'Please select at least one question wheel',
        variant: 'destructive',
      });
      return;
    }

    if (selectedQuestionTypes.length === 0) {
      toast({
        title: 'Validation Error',
        description: 'Please select at least one question type',
        variant: 'destructive',
      });
      return;
    }

    try {
      setSubmitting(true);

      // Create batch job
      const createResponse = await authFetch(
        `/api/sectors/${sectorId}/question-generation/batch-jobs`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            wheelIds: selectedWheels,
            questionTypes: selectedQuestionTypes,
            parameters,
          }),
        }
      );

      const createData = await createResponse.json();

      if (!createData.success) {
        throw new Error(createData.error || 'Failed to create batch job');
      }

      const jobId = createData.job.id;

      // Start the batch job
      const startResponse = await authFetch(
        `/api/sectors/${sectorId}/question-generation/batch-jobs/${jobId}/start`,
        {
          method: 'POST',
        }
      );

      const startData = await startResponse.json();

      if (!startData.success) {
        throw new Error(startData.error || 'Failed to start batch job');
      }

      toast({
        title: 'Batch Job Started',
        description: `Job created with ${calculateTotalItems()} tasks`,
      });

      // Navigate to monitoring page
      navigate(`/sectors/${sectorId}/question-generation/monitor/${jobId}`);
    } catch (error: any) {
      console.error('Error starting batch job:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to start batch job',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Batch Question Generation</h1>
        <p className="text-muted-foreground mt-2">
          Generate questions across multiple wheels and question types for {sectorName}
        </p>
      </div>

      {/* Configuration Cards */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Question Wheels Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Select Question Wheels
            </CardTitle>
            <CardDescription>
              Choose which wheels to generate questions for ({selectedWheels.length} selected)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {wheels.length === 0 ? (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    No question wheels found. Create wheels first before generating questions.
                  </AlertDescription>
                </Alert>
              ) : (
                wheels.map((wheel) => (
                  <div
                    key={wheel.id}
                    className="flex items-start space-x-3 p-3 rounded-lg hover:bg-accent cursor-pointer"
                    onClick={() => handleWheelToggle(wheel.id)}
                  >
                    <Checkbox
                      id={`wheel-${wheel.id}`}
                      checked={selectedWheels.includes(wheel.id)}
                      onCheckedChange={() => handleWheelToggle(wheel.id)}
                    />
                    <div className="flex-1">
                      <Label
                        htmlFor={`wheel-${wheel.id}`}
                        className="font-medium cursor-pointer"
                      >
                        {wheel.name}
                      </Label>
                      {wheel.description && (
                        <p className="text-sm text-muted-foreground mt-1">
                          {wheel.description}
                        </p>
                      )}
                      <div className="flex gap-2 mt-2">
                        {wheel.category && (
                          <Badge variant="outline">{wheel.category}</Badge>
                        )}
                        <Badge variant="secondary">
                          {wheel.question_count} questions
                        </Badge>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Question Types Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              Select Question Types
            </CardTitle>
            <CardDescription>
              Choose which types of questions to generate ({selectedQuestionTypes.length} selected)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {QUESTION_TYPES.map((qt) => (
                <div key={qt.id} className="space-y-2">
                  <div
                    className="flex items-start space-x-3 p-3 rounded-lg hover:bg-accent cursor-pointer"
                    onClick={() => handleQuestionTypeToggle(qt.id)}
                  >
                    <Checkbox
                      id={`qt-${qt.id}`}
                      checked={selectedQuestionTypes.includes(qt.id)}
                      onCheckedChange={() => handleQuestionTypeToggle(qt.id)}
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <Label
                          htmlFor={`qt-${qt.id}`}
                          className="font-medium cursor-pointer"
                        >
                          {qt.name}
                        </Label>
                        <Badge variant="outline" className="ml-2">
                          P{qt.priority}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {qt.description}
                      </p>
                    </div>
                  </div>

                  {/* Parameters */}
                  {selectedQuestionTypes.includes(qt.id) && (
                    <div className="ml-10 space-y-2 bg-accent/50 p-3 rounded-lg">
                      {qt.parameters.map((param) => (
                        <div key={param.name} className="flex items-center gap-3">
                          <Label className="text-sm w-40 capitalize">
                            {param.name.replace(/([A-Z])/g, ' $1').trim()}:
                          </Label>
                          <Input
                            type="number"
                            min={param.min}
                            max={param.max}
                            value={parameters[qt.id]?.[param.name] || param.defaultValue}
                            onChange={(e) =>
                              handleParameterChange(
                                qt.id,
                                param.name,
                                parseInt(e.target.value) || param.defaultValue
                              )
                            }
                            className="w-24"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Summary and Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Batch Summary</CardTitle>
          <CardDescription>
            Review your batch configuration before starting
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="text-center p-4 rounded-lg bg-accent">
              <div className="text-3xl font-bold">{selectedWheels.length}</div>
              <div className="text-sm text-muted-foreground mt-1">Wheels Selected</div>
            </div>
            <div className="text-center p-4 rounded-lg bg-accent">
              <div className="text-3xl font-bold">{selectedQuestionTypes.length}</div>
              <div className="text-sm text-muted-foreground mt-1">Question Types</div>
            </div>
            <div className="text-center p-4 rounded-lg bg-accent">
              <div className="text-3xl font-bold">{calculateTotalItems()}</div>
              <div className="text-sm text-muted-foreground mt-1">Total Tasks</div>
            </div>
          </div>

          <Alert>
            <CheckCircle2 className="h-4 w-4" />
            <AlertDescription>
              This batch will generate approximately <strong>{estimateTotalQuestions()}</strong> questions
              across {calculateTotalItems()} generation tasks.
            </AlertDescription>
          </Alert>

          <div className="flex gap-3 justify-end">
            <Button
              variant="outline"
              onClick={() => navigate(`/sectors/${sectorId}/question-wheels`)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleStartBatch}
              disabled={submitting || selectedWheels.length === 0 || selectedQuestionTypes.length === 0}
            >
              {submitting ? (
                <>
                  <Settings className="mr-2 h-4 w-4 animate-spin" />
                  Starting Batch...
                </>
              ) : (
                <>
                  <Play className="mr-2 h-4 w-4" />
                  Start Batch Generation
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
